import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import LandingNavbar from '../../components/LandingNavbar';
import LandingFooter from '../../components/LandingFooter';
import { getCategoryById, getArticleBySlug, getArticlesByCategory } from '../../data/helpArticles';
import './Help.css';

/**
 * Help Article Page
 * Shows a single help article
 * Logged in: uses app Layout | Not logged in: uses LandingNavbar/Footer
 * URL: /help/:category/:slug
 */
const HelpArticle = () => {
  const { category, slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [feedback, setFeedback] = useState(null);
  const categoryData = getCategoryById(category);
  const article = getArticleBySlug(category, slug);

  if (!categoryData) {
    return <Navigate to="/help" replace />;
  }

  if (!article) {
    return <Navigate to={`/help/${category}`} replace />;
  }

  const categoryArticles = getArticlesByCategory(category);
  const currentIndex = categoryArticles.findIndex(a => a.slug === slug);
  const prevArticle = currentIndex > 0 ? categoryArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < categoryArticles.length - 1 ? categoryArticles[currentIndex + 1] : null;

  const relatedArticles = categoryArticles
    .filter(a => a.slug !== slug)
    .slice(0, 3);

  const content = (
    <div className="help-container">
      <nav className="help-breadcrumb">
        <Link to="/help">Help Center</Link>
        <span className="help-breadcrumb-separator">/</span>
        <Link to={`/help/${category}`}>{categoryData.name}</Link>
        <span className="help-breadcrumb-separator">/</span>
        <span className="help-breadcrumb-current">{article.title}</span>
      </nav>

      <article className="help-article">
        <header className="help-article-header">
          <h1>{article.title}</h1>
          {article.summary && (
            <p className="help-article-lead">{article.summary}</p>
          )}
        </header>

        <div className="help-article-content">
          {typeof article.content === 'string' ? (
            <div dangerouslySetInnerHTML={{ __html: article.content }} />
          ) : (
            article.content
          )}
        </div>

        <div className="help-feedback-section">
          {feedback === null ? (
            <>
              <p className="help-feedback-question">Kya yeh article helpful tha?</p>
              <div className="help-feedback-buttons">
                <button className="help-feedback-btn help-feedback-yes" onClick={() => setFeedback('yes')}>
                  Haan
                </button>
                <button className="help-feedback-btn help-feedback-no" onClick={() => setFeedback('no')}>
                  Nahi
                </button>
              </div>
            </>
          ) : (
            <div className="help-feedback-thanks">
              {feedback === 'yes' ? (
                <p>Shukriya! Aapka feedback helpful hai.</p>
              ) : (
                <p>Shukriya feedback ke liye. Hum isse better banayenge.</p>
              )}
            </div>
          )}
        </div>
      </article>

      <nav className="help-article-nav">
        {prevArticle ? (
          <Link to={`/help/${category}/${prevArticle.slug}`} className="help-article-nav-prev">
            <span className="help-article-nav-label">Pichla Article</span>
            <span className="help-article-nav-title">{prevArticle.title}</span>
          </Link>
        ) : (
          <div className="help-article-nav-empty"></div>
        )}

        {nextArticle ? (
          <Link to={`/help/${category}/${nextArticle.slug}`} className="help-article-nav-next">
            <span className="help-article-nav-label">Agla Article</span>
            <span className="help-article-nav-title">{nextArticle.title}</span>
          </Link>
        ) : (
          <div className="help-article-nav-empty"></div>
        )}
      </nav>

      {relatedArticles.length > 0 && (
        <div className="help-related-section">
          <h3 className="help-related-title">Is Category Ke Aur Articles</h3>
          <div className="help-related-list">
            {relatedArticles.map((related) => (
              <Link
                key={related.slug}
                to={`/help/${category}/${related.slug}`}
                className="help-related-item"
              >
                <span className="help-related-item-title">{related.title}</span>
                {related.summary && (
                  <span className="help-related-item-summary">{related.summary}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="help-back-link">
        <Link to={`/help/${category}`}>
          {categoryData.name} ke sabhi articles dekhein
        </Link>
      </div>
    </div>
  );

  if (isAuthenticated) {
    return <Layout>{content}</Layout>;
  }

  return (
    <>
      <LandingNavbar />
      {content}
      <LandingFooter />
    </>
  );
};

export default HelpArticle;
